import React, { useState, useEffect } from 'react';
import { Menu, Select, Drawer, Grid } from 'antd';
// PageHeader not used here
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import logo from '../assets/images/header/logo.svg';
import emblem from '../assets/images/header/national-emblem.png';
import menuIcon from '../assets/images/menu.svg';
import searchIcon from '../assets/images/Search.svg';
import AppFooter from './AppFooter';
import HeaderBar from './HeaderBar';
import '../styles/AppLayout.css';

// Layout helpers not destructured (we use Antd components where needed in children)

// Navigation links configuration
const NAV_LINKS = [
  { key: '/', name: 'मुख्यपृष्ठ', href: '/' },
  { key: '/gram-panchayat', name: 'आपली पंचायत', href: '/gram-panchayat' },
  { key: '/our-village', name: 'आपला तालुका', href: '/our-village' },
  { key: '/schemes-projects', name: 'योजना आणि प्रकल्प', href: '/schemes-projects' },
  { key: '/citizen-services', name: 'नागरिक सेवा', href: '/citizen-services' },
  { key: '/transparency-reports', name: 'अर्थ संकल्प', href: '/transparency-reports' },
  { key: '/photo-gallery', name: 'फोटो गॅलरी', href: '/photo-gallery' },
  { key: '/digital-library', name: 'डिजिटल ग्रंथालय', href: '/digital-library' },
  { key: '/ceremony', name: 'कार्यक्रम', href: '/ceremony' },
  { key: '/birthday-wishes', name: 'न्यूज आणि मीडिया', href: '/birthday-wishes' },
  { key: '/rti', name: 'माहितीचा अधिकार', href: '/rti' },
  { key: '/samruddha-panchayat', name: 'मुख्यमंत्री समृद्ध पंचायत राज अभियान', href: '/samruddha-panchayat' },
];

export default function MainLayout({ children }) {
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const path = location.pathname || '/';
  const screens = Grid.useBreakpoint();
  const isMobile = !screens.xl;
  const [drawerOpen, setDrawerOpen] = useState(false);
  // track previous pathname to distinguish same-page anchor clicks
  const prevPathRef = React.useRef(location.pathname);

  // Scroll/anchor behaviour on route change
  useEffect(() => {
    // Improved behavior:
    // - If only the hash changed and pathname is same as previous, try a quick immediate scroll (short retries).
    // - If navigating to a different pathname with a hash, allow longer retries (content may render async).
    const hash = (location && location.hash) || '';
    const pathname = location.pathname || '/';

    const scrollToElement = (id) => {
      try {
        const el = document.getElementById(id) || document.querySelector(`[name="${id}"]`);
        if (el && typeof el.scrollIntoView === 'function') {
          el.scrollIntoView({ behavior: 'smooth', block: 'start' });
          return true;
        }
      } catch (err) {
        // ignore
      }
      return false;
    };

    const doFallbackScroll = (hashVal = '') => {
      const targets = [
        document.querySelector('.content-container'),
        document.querySelector('.page-wrapper'),
        document.documentElement,
        document.body,
      ];

      let scrolled = false;
      for (const t of targets) {
        if (!t) continue;
        try {
          if (typeof t.scrollTo === 'function') {
            t.scrollTo({ top: 0, behavior: 'smooth' });
            scrolled = true;
          } else if (typeof t.scrollTop !== 'undefined') {
            t.scrollTop = 0;
            scrolled = true;
          }
        } catch (err) {
          // ignore
        }
      }

      try {
        if (typeof window.scrollTo === 'function') window.scrollTo({ top: 0, behavior: 'smooth' });
      } catch (err) {
        try { window.scrollTo(0, 0); } catch (e) { }
      }

      console.debug('[MainLayout] scroll-top fallback executed for', pathname, 'scrolled:', scrolled, 'hash:', hashVal);
    };

    const runHashScroll = () => {
      if (!hash) return false;
      const id = hash.startsWith('#') ? hash.slice(1) : hash;

      // If same-page anchor (pathname didn't change), do a quick attempt (short retries)
      if (prevPathRef.current === pathname) {
        // immediate attempt + a couple quick retries
        if (scrollToElement(id)) {
          console.debug('[MainLayout] immediate scrolled to hash target', id);
          return true;
        }
        let attempts = 0;
        const quickRetry = () => {
          attempts += 1;
          if (scrollToElement(id)) {
            console.debug('[MainLayout] quick-retry scrolled to hash target', id, 'attempts:', attempts);
            return;
          }
          if (attempts < 6) {
            window.setTimeout(quickRetry, 50);
          } else {
            console.debug('[MainLayout] quick retries exhausted for', id);
            doFallbackScroll(id);
          }
        };
        window.setTimeout(quickRetry, 50);
        return true;
      }

      // Different page: content may render async -> longer retry loop
      let attempts = 0;
      const maxAttempts = 30; // ~1.5s
      const tryFind = () => {
        attempts += 1;
        if (scrollToElement(id)) {
          console.debug('[MainLayout] scrolled to hash target', id, 'attempts:', attempts);
          return;
        }
        if (attempts < maxAttempts) {
          window.setTimeout(tryFind, 50);
        } else {
          console.debug('[MainLayout] hash target not found after retries', id);
          doFallbackScroll(id);
        }
      };
      tryFind();
      return true;
    };

    const id = window.setTimeout(() => {
      if (hash) {
        runHashScroll();
      } else {
        doFallbackScroll();
      }
      // update previous pathname after handling scroll
      prevPathRef.current = pathname;
    }, 40);

    return () => window.clearTimeout(id);
  }, [location]);

  useEffect(() => {
    document.body.setAttribute('data-language', i18n.language || 'mr');
  }, [i18n.language]);

  // Header layout helpers
  const leftStyle = { display: 'flex', alignItems: 'center', gap: 12, minWidth: 'fit-content' };
  const logoStyle = { height: isMobile ? 30 : 38, display: 'block', color: 'var(--color-primary)' };
  const menuWrapperStyle = { flex: 1, display: 'flex', justifyContent: 'center', minWidth: 0, overflow: 'hidden' };
  const menuStyle = {
    backgroundColor: 'transparent',
    borderBottom: 'none',
    lineHeight: '72px',
    display: 'flex',
    justifyContent: 'center',
    fontFamily: 'Poppins, sans-serif',
    fontWeight: 600,
    fontSize: 14,
    letterSpacing: 0,
    width: '100%',
    minWidth: 0,
  };
  const rightStyle = { display: 'flex', alignItems: 'center', gap: 12 };

  // compute selected key based on current path
  let selectedKey = '/';
  if (path.startsWith('/gram-panchayat')) selectedKey = '/gram-panchayat';
  else if (path.startsWith('/our-village')) selectedKey = '/our-village';
  else if (path.startsWith('/schemes-projects')) selectedKey = '/schemes-projects';
  else if (path.startsWith('/citizen-services')) selectedKey = '/citizen-services';
  else if (path.startsWith('/digital-library')) selectedKey = '/digital-library';
  else if (path.startsWith('/photo-gallery')) selectedKey = '/photo-gallery';
  else if (path.startsWith('/transparency-reports')) selectedKey = '/transparency-reports';
  else if (path.startsWith('/donation')) selectedKey = '/donation';
  else if (path.startsWith('/rti')) selectedKey = '/rti';
  else if (path.startsWith('/samruddha-panchayat')) selectedKey = '/samruddha-panchayat';
  else if (path.startsWith('/ceremony')) selectedKey = '/ceremony';
  else if (path.startsWith('/birthday-wishes')) selectedKey = '/birthday-wishes';

  const changeLang = lng => {
    i18n.changeLanguage(lng || 'mr');
    document.body.setAttribute('data-language', lng || 'mr');
  };

  return (
    <>
      <div className="sticky-header">
        <HeaderBar
          navProps={{
            emblem,
            logo,
            isMobile,
            selectedKey,
            t,
            menuIcon,
            searchIcon,
            menuWrapperStyle,
            menuStyle,
            rightStyle,
            leftStyle,
            logoStyle,
            changeLang,
            i18n,
            drawerOpen,
            setDrawerOpen,
            Menu,
            Select,
            Link,
          }}
        />
      </div>
      <div className="page-wrapper">
        <Drawer
          title={<img loading="lazy" src={logo} alt="logo" style={{ height: 28 }} />}
          placement="right"
          onClose={() => setDrawerOpen(false)}
          open={drawerOpen}
        >
          <Menu
            mode="vertical"
            selectedKeys={[selectedKey]}
            style={{ border: 'none' }}
            onClick={() => {
              setDrawerOpen(false);
              try {
                window.dispatchEvent(new CustomEvent('closeAppModals'));
              } catch (e) {
                const ev = document.createEvent('Event');
                ev.initEvent('closeAppModals', true, true);
                window.dispatchEvent(ev);
              }
            }}
          >
            {NAV_LINKS.map((link) => (
              <Menu.Item key={link.key} onClick={() => setDrawerOpen(false)}>
                <Link to={link.href}>{link.name}</Link>
              </Menu.Item>
            ))}
          </Menu>
          {/* language select removed from mobile drawer; language is available in top header */}
        </Drawer>
        <div className="content-container" style={{ minHeight: 'calc(100vh - 200px)', background: 'transparent' }}>
          {children}
        </div>
      </div>
      <AppFooter />
    </>
  );
}